import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Ticket, 
  Plus, 
  Clock, 
  CheckCircle2, 
  ChevronRight,
  ChevronDown,
  X,
  Loader2,
  Filter,
  Mail,
  Calendar,
  Tag,
  AlertTriangle,
  Edit3,
  Send
} from 'lucide-react';
import { ticketsApi, SupportTicket, TicketStats } from '../lib/api';
import toast from 'react-hot-toast';
import { useAnalytics, AnalyticsEvents } from '../hooks/useAnalytics';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] }
};

const categories = [
  { value: 'GENERAL', label: 'General Inquiry' },
  { value: 'BILLING', label: 'Billing & Payments' },
  { value: 'TECHNICAL', label: 'Technical Issue' },
  { value: 'COURSE', label: 'Course Related' },
  { value: 'ACCOUNT', label: 'Account Issue' },
  { value: 'OTHER', label: 'Other' },
];

const priorities = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
];

// Simplified status colors
const statusColors: Record<string, string> = {
  OPEN: 'bg-info/20 text-info border-info/30',
  RESOLVED: 'bg-success/20 text-success border-success/30',
  CLOSED: 'bg-white/10 text-white/50 border-white/20',
};

// Simplified status labels
const statusLabels: Record<string, string> = {
  OPEN: 'Open',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
};

// Priority colors and styles
const priorityStyles: Record<string, { bg: string; text: string; border: string }> = {
  LOW: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  MEDIUM: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30' },
  HIGH: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30' },
};

// Filter options
const filterOptions = [
  { value: 'all', label: 'All Tickets' },
  { value: 'OPEN', label: 'Open' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'CLOSED', label: 'Closed' },
];

export default function SupportPage() {
  const { trackEvent } = useAnalytics();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [stats, setStats] = useState<TicketStats | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({ description: '', category: '', priority: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [, setIsAddingUpdate] = useState(false);

  // New ticket form state
  const [newTicket, setNewTicket] = useState({
    subject: '',
    description: '',
    category: 'GENERAL',
    priority: 'MEDIUM',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchTickets();
    fetchStats();
  }, [statusFilter]);

  const fetchTickets = async () => {
    try {
      setIsLoading(true);
      const response = await ticketsApi.getAll({ 
        status: statusFilter !== 'all' ? statusFilter : undefined 
      });
      if (response.data.success && response.data.data) {
        setTickets(response.data.data.tickets);
      }
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await ticketsApi.getStats();
      if (response.data.success && response.data.data) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchTicketDetails = async (ticketId: string) => {
    try {
      const response = await ticketsApi.getById(ticketId);
      if (response.data.success && response.data.data) {
        setSelectedTicket(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch ticket details:', error);
    }
  };

  const handleCreateTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTicket.subject || !newTicket.description) return;

    setIsSubmitting(true);
    try {
      const response = await ticketsApi.create(newTicket);
      if (response.data.success) {
        // Track ticket creation (no sensitive content)
        trackEvent(AnalyticsEvents.SUPPORT_TICKET_CREATED, {
          category: newTicket.category,
          priority: newTicket.priority,
        });
        setIsCreating(false);
        setNewTicket({ subject: '', description: '', category: 'GENERAL', priority: 'MEDIUM' });
        fetchTickets();
        fetchStats();
      }
    } catch (error) {
      console.error('Failed to create ticket:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartEdit = () => {
    if (selectedTicket) {
      setEditData({
        description: selectedTicket.description,
        category: selectedTicket.category,
        priority: selectedTicket.priority || 'MEDIUM',
      });
      setIsEditing(true);
    }
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTicket) return;

    setIsSaving(true);
    try {
      await ticketsApi.updateTicket(selectedTicket.id, {
        description: editData.description,
        category: editData.category,
        priority: editData.priority,
      });
      setIsEditing(false);
      toast.success('Ticket updated successfully!');
      fetchTicketDetails(selectedTicket.id);
      fetchTickets(); // Refresh the list too
    } catch (error) {
      console.error('Failed to update ticket:', error);
      toast.error('Failed to update ticket. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseTicket = async () => {
    if (!selectedTicket) return;

    setIsClosing(true);
    try {
      await ticketsApi.close(selectedTicket.id);
      toast.success('Ticket closed successfully');
      setSelectedTicket(null);
      fetchTickets();
      fetchStats();
    } catch (error) {
      console.error('Failed to close ticket:', error);
      toast.error('Failed to close ticket. Please try again.');
    } finally {
      setIsClosing(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getFilterLabel = () => {
    return filterOptions.find(f => f.value === statusFilter)?.label || 'All Tickets';
  };

  // Map old statuses to simplified ones for display
  const getDisplayStatus = (status: string) => {
    if (status === 'IN_PROGRESS' || status === 'WAITING_RESPONSE') {
      return 'OPEN';
    }
    return status;
  };

  return (
    <div className="bg-void min-h-screen">
      {/* Header */}
      <section className="relative py-10 lg:py-16 overflow-hidden border-b border-white/[0.06]">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-[600px] h-[300px] bg-slate/15 rounded-full blur-[120px]" />
        </div>

        <div className="container-luxury relative">
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeUp}
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          >
            <div>
              <div className="flex items-center gap-2 text-white/40 text-sm mb-3">
                <Link to="/help" className="hover:text-white transition-colors">Help Center</Link>
                <ChevronRight className="w-4 h-4" />
                <span className="text-white">My Tickets</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white">Support Tickets</h1>
              <p className="mt-2 text-white/50">Manage your support requests</p>
            </div>
            <button
              onClick={() => setIsCreating(true)}
              className="btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Ticket
            </button>
          </motion.div>
        </div>
      </section>

      <div className="container-luxury py-8">
        {/* Stats Bar & Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8"
        >
          {/* Quick Stats */}
          {stats && (
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <span className="text-white/50 text-sm">Total:</span>
                <span className="text-white font-semibold">{stats.total}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-info" />
                <span className="text-white/50 text-sm">Open:</span>
                <span className="text-white font-semibold">{stats.open + stats.inProgress + stats.waitingResponse}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-success" />
                <span className="text-white/50 text-sm">Resolved:</span>
                <span className="text-white font-semibold">{stats.resolved}</span>
              </div>
            </div>
          )}

          {/* Filter Dropdown */}
          <div className="relative">
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm
                       bg-white/[0.03] hover:bg-white/[0.06] 
                       border border-white/[0.08] transition-all duration-200"
            >
              <Filter className="w-4 h-4 text-white/40" />
              <span className="text-white">{getFilterLabel()}</span>
              <ChevronDown className={`w-4 h-4 text-white/40 transition-transform ${isFilterOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {isFilterOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setIsFilterOpen(false)} 
                  />
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.96 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-2 w-48 z-50 
                             bg-[#16161d]/95 backdrop-blur-xl 
                             border border-white/[0.08] rounded-xl shadow-2xl overflow-hidden"
                  >
                    <div className="p-1.5">
                      {filterOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setStatusFilter(option.value);
                            setIsFilterOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                            statusFilter === option.value
                              ? 'bg-slate/30 text-white'
                              : 'text-white/60 hover:text-white hover:bg-white/[0.05]'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {option.value !== 'all' && (
                              <span className={`w-2 h-2 rounded-full ${
                                option.value === 'OPEN' ? 'bg-info' :
                                option.value === 'RESOLVED' ? 'bg-success' : 'bg-white/30'
                              }`} />
                            )}
                            {option.label}
                          </div>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Tickets List */}
        {isLoading ? (
          <div className="card flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-slate-light animate-spin" />
          </div>
        ) : tickets.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="card text-center py-16"
          >
            <div className="w-20 h-20 rounded-2xl bg-slate/20 flex items-center justify-center mx-auto mb-6">
              <Ticket className="w-10 h-10 text-slate-light" />
            </div>
            <h3 className="text-2xl font-semibold text-white mb-3">No tickets yet</h3>
            <p className="text-white/50 mb-8 max-w-md mx-auto">
              Create your first support ticket to get help from our team. We typically respond within 24 hours.
            </p>
            <button
              onClick={() => setIsCreating(true)}
              className="btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Ticket
            </button>
          </motion.div>
        ) : (
          <div className="grid gap-4">
            {tickets.map((ticket, index) => {
              const displayStatus = getDisplayStatus(ticket.status);
              return (
                <motion.button
                  key={ticket.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => fetchTicketDetails(ticket.id)}
                  className="card card-hover w-full text-left group"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-coral text-xs font-mono font-semibold">
                          #{ticket.zohoTicketNumber}
                        </span>
                        <span className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${statusColors[displayStatus] || statusColors.OPEN}`}>
                          {statusLabels[displayStatus] || 'Open'}
                        </span>
                        {(() => {
                          const priority = ticket.priority || 'MEDIUM';
                          const style = priorityStyles[priority] || priorityStyles.MEDIUM;
                          return (
                            <span className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${style.bg} ${style.text} ${style.border}`}>
                              {priority}
                            </span>
                          );
                        })()}
                      </div>
                      <h3 className="font-semibold text-white text-lg truncate group-hover:text-slate-light transition-colors">
                        {ticket.subject}
                      </h3>
                      <p className="text-white/40 text-sm mt-1.5 line-clamp-2">
                        {ticket.description}
                      </p>
                      <div className="flex items-center gap-5 mt-4 text-white/30 text-xs">
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5" />
                          {formatDate(ticket.createdAt)}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5" />
                          Responses via email
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-white/30 group-hover:translate-x-1 group-hover:text-white/50 transition-all flex-shrink-0 mt-2" />
                  </div>
                </motion.button>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Ticket Modal */}
      <AnimatePresence>
        {isCreating && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
            onClick={() => setIsCreating(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-[#12121a] border border-white/10 rounded-2xl p-6 shadow-2xl"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Create Support Ticket</h2>
                <button
                  onClick={() => setIsCreating(false)}
                  className="p-2 rounded-lg hover:bg-white/5 transition-colors"
                >
                  <X className="w-5 h-5 text-white/50" />
                </button>
              </div>

              <form onSubmit={handleCreateTicket} className="space-y-5">
                <div>
                  <label className="label">Subject *</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="Brief description of your issue"
                    value={newTicket.subject}
                    onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Category</label>
                    <select
                      className="input"
                      value={newTicket.category}
                      onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                    >
                      {categories.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label">Priority</label>
                    <select
                      className="input"
                      value={newTicket.priority}
                      onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                    >
                      {priorities.map((pri) => (
                        <option key={pri.value} value={pri.value}>
                          {pri.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="label">Description *</label>
                  <textarea
                    className="input min-h-[150px]"
                    placeholder="Please describe your issue in detail..."
                    value={newTicket.description}
                    onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                    required
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsCreating(false)}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary flex-1"
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Ticket
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Ticket Detail Modal */}
      <AnimatePresence>
        {selectedTicket && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
            onClick={() => { setSelectedTicket(null); setIsAddingUpdate(false); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-xl bg-[#12121a] border border-white/10 rounded-2xl p-6 shadow-2xl"
            >
              {/* Header */}
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-coral text-sm font-mono font-semibold">
                      #{selectedTicket.zohoTicketNumber}
                    </span>
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${statusColors[getDisplayStatus(selectedTicket.status)] || statusColors.OPEN}`}>
                      {statusLabels[getDisplayStatus(selectedTicket.status)] || 'Open'}
                    </span>
                  </div>
                  <h2 className="text-xl font-bold text-white">{selectedTicket.subject}</h2>
                </div>
                <button
                  onClick={() => { setSelectedTicket(null); setIsEditing(false); }}
                  className="p-2 rounded-lg hover:bg-white/5 transition-colors"
                >
                  <X className="w-5 h-5 text-white/50" />
                </button>
              </div>

              {/* Ticket Details - View Mode */}
              {!isEditing ? (
                <div className="space-y-4 mb-6">
                  {/* Meta Info */}
                  <div className="flex flex-wrap gap-3 text-sm">
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 text-white/60">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(selectedTicket.createdAt)}</span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 text-white/60">
                      <Tag className="w-4 h-4" />
                      <span>{selectedTicket.category}</span>
                    </div>
                    {(() => {
                      const priority = selectedTicket.priority || 'MEDIUM';
                      const style = priorityStyles[priority] || priorityStyles.MEDIUM;
                      return (
                        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${style.bg} ${style.text} ${style.border}`}>
                          <AlertTriangle className="w-4 h-4" />
                          <span className="font-medium">{priority}</span>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Description */}
                  <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                    <p className="text-white/70 text-sm whitespace-pre-wrap leading-relaxed">
                      {selectedTicket.description}
                    </p>
                  </div>

                  {/* Email Notice */}
                  <div className="flex items-start gap-3 p-4 rounded-xl bg-slate/10 border border-slate/20">
                    <Mail className="w-5 h-5 text-slate-light flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-white/70 text-sm">
                        Our support team will respond to your ticket via email at <span className="text-white font-medium">{selectedTicket.email}</span>
                      </p>
                      <p className="text-white/40 text-xs mt-1">
                        Typical response time: 24-48 hours
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                /* Ticket Details - Edit Mode */
                <form onSubmit={handleSaveEdit} className="space-y-4 mb-6">
                  {/* Category & Priority */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Category</label>
                      <select
                        className="input"
                        value={editData.category}
                        onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                      >
                        {categories.map((cat) => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="label">Priority</label>
                      <select
                        className="input"
                        value={editData.priority}
                        onChange={(e) => setEditData({ ...editData, priority: e.target.value })}
                      >
                        {priorities.map((pri) => (
                          <option key={pri.value} value={pri.value}>{pri.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="label">Description</label>
                    <textarea
                      className="input min-h-[120px]"
                      value={editData.description}
                      onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                      required
                    />
                  </div>

                  {/* Edit Actions */}
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setIsEditing(false)}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSaving}
                      className="btn-primary flex-1"
                    >
                      {isSaving ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                  </div>
                </form>
              )}

              {/* Actions - Only show when not editing */}
              {!isEditing && selectedTicket.status !== 'CLOSED' && selectedTicket.status !== 'RESOLVED' ? (
                <div className="flex gap-3 pt-4 border-t border-white/10">
                  <button
                    onClick={handleStartEdit}
                    className="btn-secondary flex-1"
                  >
                    <Edit3 className="w-4 h-4 mr-2" />
                    Edit Ticket
                  </button>
                  <button
                    onClick={handleCloseTicket}
                    disabled={isClosing}
                    className="btn-ghost flex-1 text-white/60 hover:text-coral hover:bg-coral/10"
                  >
                    {isClosing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <X className="w-4 h-4 mr-2" />
                        Close Ticket
                      </>
                    )}
                  </button>
                </div>
              ) : !isEditing ? (
                <div className="pt-4 border-t border-white/10 flex items-center justify-center gap-2 text-white/40">
                  <CheckCircle2 className="w-4 h-4 text-success" />
                  <span className="text-sm">This ticket is {selectedTicket.status.toLowerCase()}</span>
                </div>
              ) : null}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
